import type { FormatCurrencyOptions, SboCurrency } from '@sports-utils/money';
import { CurrencyType, getSboCurrencySymbolInfo, getSeparators, toSboCurrencyFormat } from '@sports-utils/money';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { ICustomerInfo } from '@/interface/ICustomerInfo';
import { HashType, LicenseType, PlayerType, PriceStyle, SiteStyle } from '@/interface/enum';
import Api from '@/libraries/api';
import useState from '@/libraries/composables/useState';
import { hashString } from '@/libraries/helpers/hashHelper';

export const useAccountStore = defineStore('account', () => {
    const customerInfo = ref<ICustomerInfo>({
        currency: 'TB',
        isoCurrency: 'THB',
        siteStyle: SiteStyle.Asia,
        priceStyle: PriceStyle.Malay,
        priceStyleOptions: [],
        isAcceptAnyOdds: false,
        isAcceptAnyCashOutValue: false,
        abTestGroup: {},
        playerType: PlayerType.B2C,
        license: LicenseType.IOM,
        accountId: '',
        loginName: '',
        isQ: false,
        ipCountryCode: '--',
        isEFootballEnabled: false,
        isSimulationEnabled: false,
        trackingId: '',
    });

    const [balance, setBalance] = useState<number | null>(null);

    const hashedAccountId = computed(() => hashString(customerInfo.value.accountId ?? '', HashType.MD5));

    function updateCustomerInfo(info: Partial<ICustomerInfo>) {
        customerInfo.value = {
            ...customerInfo.value,
            ...info,
        };
    }

    function abTestEnabledList() {
        return Object.keys(customerInfo.value.abTestGroup || {}).map(Number)
            .filter(key => customerInfo.value.abTestGroup[key].isUseNewFeature);
    }

    function isCurrencyInList(expectedCurrency: string[]) {
        const upperExpectedCurrency = expectedCurrency.map(c => c.toUpperCase());
        return upperExpectedCurrency.includes(customerInfo.value.currency.toUpperCase());
    }

    function isIpCountryInList(countryList: string[]) {
        return countryList.includes(customerInfo.value.ipCountryCode);
    }

    const isIomB2c = computed(() => customerInfo.value.license === LicenseType.IOM
            && customerInfo.value.playerType === PlayerType.B2C);

    const currencySymbolInfo = computed(() => getSboCurrencySymbolInfo(customerInfo.value.currency as SboCurrency));
    const displayCurrency = computed(() => currencySymbolInfo.value.symbol);

    function formatMoneyWithDecimal(value: number, option?: FormatCurrencyOptions) {
        return toSboCurrencyFormat(value, customerInfo.value.currency as SboCurrency, option);
    }

    function formatMoney(value: number) {
        return toSboCurrencyFormat(value, customerInfo.value.currency as SboCurrency, { minPrecision: 0, maxPrecision: 0 });
    }

    function formatMoneyWithSymbolAndDecimal(value: number, option?: FormatCurrencyOptions) {
        option = {
            ...option,
            withSymbol: true,
        };
        return toSboCurrencyFormat(value, customerInfo.value.currency as SboCurrency, option);
    }

    function formatMoneyWithSymbol(value: number) {
        return toSboCurrencyFormat(value, customerInfo.value.currency as SboCurrency, { minPrecision: 0, maxPrecision: 0, withSymbol: true });
    }

    const moneySeparators = computed(() => getSeparators({
        currencyType: CurrencyType.Sbo,
        currency: customerInfo.value.currency as SboCurrency,
    }));

    const updateAcceptAnyOdds = async (isAcceptAnyOdds: boolean) => {
        const oldIsAcceptAnyOdds = customerInfo.value.isAcceptAnyOdds;
        if (oldIsAcceptAnyOdds === isAcceptAnyOdds) return;
        try {
            setIsAcceptAnyOdds(isAcceptAnyOdds);
            await Api.updateIsAcceptAnyOdds(isAcceptAnyOdds);
        } catch (error) {
            setIsAcceptAnyOdds(oldIsAcceptAnyOdds);
            throw error;
        }
    };

    const updateAcceptAnyCashOutValue = async (isAcceptAnyCashOutValue: boolean) => {
        const oldIsAcceptAnyCashOutValue = customerInfo.value.isAcceptAnyCashOutValue;
        if (oldIsAcceptAnyCashOutValue === isAcceptAnyCashOutValue) return;
        try {
            setIsAcceptAnyCashOutValue(isAcceptAnyCashOutValue);
            await Api.updateIsAcceptAnyCashOutValue(isAcceptAnyCashOutValue);
        } catch (error) {
            setIsAcceptAnyCashOutValue(oldIsAcceptAnyCashOutValue);
            throw error;
        }
    };

    const updateSiteStyle = async (newSiteStyle: SiteStyle) => {
        const oldSiteStyle = customerInfo.value.siteStyle;
        if (oldSiteStyle === newSiteStyle) return;
        try {
            setSiteStyle(newSiteStyle);
            await Api.updateSiteStyle(newSiteStyle);
        } catch (error) {
            setSiteStyle(oldSiteStyle);
            throw error;
        }
    };

    const updatePriceStyle = async (newPriceStyle: PriceStyle) => {
        const oldPriceStyle = customerInfo.value.priceStyle;
        if (oldPriceStyle === newPriceStyle) return;
        try {
            setPriceStyle(newPriceStyle);
            await Api.updatePriceStyle(newPriceStyle);
        } catch (error) {
            setPriceStyle(oldPriceStyle);
            throw error;
        }
    };

    async function updateBalance() {
        const _balance = await Api.getBalance();
        setBalance(_balance);
    }

    function setPriceStyle(newPriceStyle: PriceStyle) {
        customerInfo.value.priceStyle = newPriceStyle;
    }

    function setSiteStyle(newSiteStyle: SiteStyle) {
        customerInfo.value.siteStyle = newSiteStyle;
    }

    function setIsAcceptAnyOdds(newIsAcceptAnyOdds: boolean) {
        customerInfo.value.isAcceptAnyOdds = newIsAcceptAnyOdds;
    }

    function setIsAcceptAnyCashOutValue(newIsAcceptAnyCashOutValue: boolean) {
        customerInfo.value.isAcceptAnyCashOutValue = newIsAcceptAnyCashOutValue;
    }

    return {
        customerInfo,
        updateCustomerInfo,
        isIomB2c,
        hashedAccountId,
        displayCurrency,
        currencySymbolInfo,
        isCurrencyInList,
        isIpCountryInList,
        abTestEnabledList,
        formatMoneyWithDecimal,
        formatMoney,
        formatMoneyWithSymbolAndDecimal,
        formatMoneyWithSymbol,
        moneySeparators,
        updateAcceptAnyCashOutValue,
        updateAcceptAnyOdds,
        updatePriceStyle,
        updateSiteStyle,
        balance,
        setBalance,
        updateBalance,
        setPriceStyle,
        setSiteStyle,
        setIsAcceptAnyOdds,
        setIsAcceptAnyCashOutValue,
    };
});
